import { common } from '@/features/i18n/common';
import { t } from '@/features/i18n/utils';
import { useAuth } from '@/features/auth';
import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useRef } from 'react';
import { z } from 'zod';
import { Logo } from '@/ui/components/logo';
import { Card } from '@/ui/components/card';
import { Button } from '@/ui/components/button';

export const Route = createFileRoute('/_auth/register')({
  component: RouteComponent,
  validateSearch: z.object({
    email: z.string().optional(),
    invitationToken: z.string().optional(),
    organizationName: z.string().optional(),
  }),
});

function RouteComponent() {
  const { isLoading, isAuthenticated, login } = useAuth();
  const search = Route.useSearch();
  const isRedirecting = useRef(false);
  const signup = {
    loginHint: search.email?.replace(/ /g, '%2B'),
    invitationToken: search.invitationToken,
    appState: { organizationName: search.organizationName },
  };

  if (isLoading) return null;

  if (isAuthenticated) return <Navigate to="/" />;

  if (!search.invitationToken) {
    if (!isRedirecting.current) {
      isRedirecting.current = true;
      login({ ...signup, screenHint: 'signup' });
    }
    return null;
  }

  return (
    <div className="px-5 -translate-y-5 w-full">
      <Logo className="mb-5 mx-auto" />

      <Card className="max-w-92 lg:p-6 p-5 mx-auto w-full">
        <h1 className="text-center font-semibold text-lg mb-0.5 lg:text-xl">
          {t({ en: 'Create a Volve account', nb: 'Opprett konto' })}
        </h1>

        <p className="text-center text-muted text-sm mb-4">
          {t({
            en: 'Continue with your email or SSO to get started',
            nb: 'Fortsett med e-post eller SSO for å komme i gang',
          })}
        </p>

        <Button
          size="lg"
          className="w-full"
          onClick={() => login({ ...signup, screenHint: 'signup' })}
        >
          {t({ en: 'Create a Volve account', nb: 'Opprett konto' })}
        </Button>

        <div className="flex items-center gap-3 my-4">
          <div className="h-px flex-1 bg-border" />
          <span className="text-muted text-xs">{t(common.or)}</span>
          <div className="h-px flex-1 bg-border" />
        </div>

        <Button
          size="lg"
          variant="secondary"
          className="w-full"
          onClick={() =>
            login({ ...signup, connection: import.meta.env.VITE_AUTH0_SSO_CONNECTION })
          }
        >
          {t({ en: 'Sign up with SSO', nb: 'Registrer med SSO' })}
        </Button>
      </Card>
    </div>
  );
}
